
import { Routes } from '@angular/router';
import { BackOfficeComponent } from './components/back-office/back-office.component';
import { DashboardComponent } from './components/dashboard/dashboard.component';
import { ProductManagementComponent } from './components/product-management/product-management.component';
import { CustomerManagementComponent } from './components/customer-management/customer-management.component';
import { LoginComponent } from './components/login/login.component';
import { authGuard } from './auth.guard';
import { EmployeesManagementComponent } from './components/employees-management/employees-management.component';
import { PromotionsManagementComponent } from './components/promotions-management/promotions-management.component';
import { SettingsComponent } from './components/settings/settings.component';
import { PinLoginComponent } from './components/pin-login/pin-login.component';
import { DataImporterComponent } from './components/data-importer/data-importer.component';
import { RegisterComponent } from './components/register/register.component';
import { PrescriptionFormComponent } from './components/prescription-form/prescription-form.component';
import { MedicalCertificateComponent } from './components/medical-certificate/medical-certificate.component'; 
import { VerifyCertificateComponent } from './components/verify-certificate/verify-certificate.component'; // Import Verify Component
import { ReportsComponent } from './components/reports/reports.component';
import { TeleMedicineComponent } from './components/tele-medicine/tele-medicine.component';
import { BillingPageComponent } from './components/billing-page/billing-page.component';
import { PharmacyComponent } from './components/pharmacy/pharmacy.component';
import { ComplianceReportsComponent } from './components/compliance-reports/compliance-reports.component';
import { PlatformDashboardComponent } from './components/platform-admin/platform-dashboard.component';
import { PosComponent } from './components/pos/pos.component';
import { PartnerNetworkComponent } from './components/partner-network/partner-network.component';
import { OnboardingComponent } from './components/onboarding/onboarding.component';
import { ForgotPasswordComponent } from './components/forgot-password/forgot-password.component';
import { UpdatePasswordComponent } from './components/update-password/update-password.component';
import { AuditLogsComponent } from './components/audit-logs/audit-logs.component';
import { MyProfileComponent } from './components/my-profile/my-profile.component';

export const routes: Routes = [
  {
    path: 'portal',
    loadChildren: () => import('./customer-portal/routes').then(m => m.CUSTOMER_PORTAL_ROUTES)
  },

  { path: 'login', component: LoginComponent, title: 'Clinic Staff Login' },
  { path: 'register', component: RegisterComponent, title: 'Clinic Staff Signup' },
  { path: 'forgot-password', component: ForgotPasswordComponent, title: 'Reset Password' },
  { path: 'update-password', component: UpdatePasswordComponent, title: 'Set New Password' },
  { path: 'onboarding', component: OnboardingComponent, title: 'Setup Your Store', canActivate: [authGuard] },
  { path: '', component: PinLoginComponent, title: 'Clinic Access - PIN' },
  
  { 
    path: 'pos', 
    component: PosComponent, 
    title: 'Patient Intake Kiosk',
    canActivate: [authGuard],
    data: { roles: ['cashier', 'staff', 'admin', 'owner'] }
  },
  
  // Public Verification Route (No Auth Guard)
  { 
    path: 'verify/:id', 
    component: VerifyCertificateComponent, 
    title: 'Verify Certificate - CHUAMKAN' 
  },

  // Print Routes
  { path: 'print/prescription/:prescriptionId', component: PrescriptionFormComponent, title: 'Print Prescription' },
  { path: 'print/certificate/:prescriptionId', component: MedicalCertificateComponent, title: 'Medical Certificate' },
  
  {
    path: 'platform',
    component: PlatformDashboardComponent,
    title: 'MSP Platform Admin',
    canActivate: [authGuard],
    data: { roles: ['platform_admin'] }
  },

  {
    path: 'admin',
    component: BackOfficeComponent,
    canActivate: [authGuard],
    children: [
      { path: '', redirectTo: 'dashboard', pathMatch: 'full' },
      
      { 
        path: 'dashboard', 
        component: DashboardComponent, 
        title: 'Clinic Dashboard',
        canActivate: [authGuard],
        data: { roles: ['admin', 'owner', 'doctor'] }
      },
      { 
        path: 'customers', 
        component: CustomerManagementComponent, 
        title: 'Patient Registration Database',
      },
      {
        path: 'pharmacy',
        component: PharmacyComponent,
        title: 'Pharmacy Station',
        canActivate: [authGuard],
        data: { roles: ['cashier', 'admin', 'owner', 'staff', 'accountant'] } 
      },
      { 
        path: 'tele-medicine', 
        component: TeleMedicineComponent, 
        title: 'Doctor Portal', 
        canActivate: [authGuard], 
        data: { 
          feature: 'telemedicine',
          roles: ['doctor', 'admin', 'owner'] 
        } 
      },
      { 
        path: 'reports', 
        component: ReportsComponent, 
        title: 'Clinic Reports', 
        canActivate: [authGuard], 
        data: { feature: 'reporting', roles: ['admin', 'owner', 'accountant'] }
      },
      { 
        path: 'compliance', 
        component: ComplianceReportsComponent, 
        title: 'Cannabis Compliance',
        canActivate: [authGuard],
        data: { feature: 'compliance_reports', roles: ['admin', 'owner'] }
      },
      { 
        path: 'employees', 
        component: EmployeesManagementComponent, 
        title: 'Staff Management',
        canActivate: [authGuard],
        data: { roles: ['admin', 'owner'] }
      },
      { 
        path: 'audit-logs', 
        component: AuditLogsComponent, 
        title: 'Security Audit Logs',
        canActivate: [authGuard],
        data: { roles: ['admin', 'owner'] }
      },
      { 
        path: 'promotions', 
        component: PromotionsManagementComponent, 
        title: 'Loyalty Program', 
        canActivate: [authGuard], 
        data: { feature: 'loyalty', roles: ['admin', 'owner'] }
      },
      {
        path: 'partner-network',
        component: PartnerNetworkComponent,
        title: 'Partner Network',
        canActivate: [authGuard],
        data: { feature: 'partner_network', roles: ['admin', 'owner'] }
      },
      { 
        path: 'billing', 
        component: BillingPageComponent, 
        title: 'Subscription',
        canActivate: [authGuard],
        data: { roles: ['owner', 'admin', 'platform_admin'] }
      },
      { 
        path: 'importer', 
        component: DataImporterComponent, 
        title: 'Data Importer',
        canActivate: [authGuard],
        data: { roles: ['admin', 'owner'] }
      },
      { 
        path: 'settings', 
        component: SettingsComponent, 
        title: 'System Settings',
        canActivate: [authGuard],
        data: { roles: ['admin', 'owner'] }
      },
      { 
        path: 'profile', 
        component: MyProfileComponent, 
        title: 'My Profile',
        canActivate: [authGuard]
      },
    ]
  },
  { path: '**', redirectTo: '' }
];
